import WRAP from "WRAP-JS";
import { WX_ID_GASS_SCALE } from "../../../store/WeatherContentsState";
import { formatUTCDate, parseUTCDate, _orderBy } from "../../../utils/FPEUtils";
import TimeController from "../helper/TimeController";
import LegendGassScale from "../legend/LegendGassScale";
import { dhConfigGassCling, dhConfigGassHvis, layerConfigCling, layerConfigHvis } from "./config/ConfigGassScale";
import MissingDataObserver from "../helper/MissingDataObserver";
import { COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER, COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_CLING, COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_HVIS } from "../../../constants/ErrorMessage";

export const GASS_HVIS = 'GASS_HVIS';
export const GASS_CLING = 'GASS_CLING';
const GASS_ALL = 'GASS_ALL';

const MISSING_THRESHOLD = {
  [GASS_HVIS]: 0,
  [GASS_CLING]: 0
}

class GassScale {
  static NAME = WX_ID_GASS_SCALE;

  // layer
  layerGassHvis;
  layerGassCling;
  // dh
  dhGassHvis;
  dhGassCling;

  dhAutoUpdate = {
    [GASS_HVIS]: true,
    [GASS_CLING]: true
  }
  dataInitialize = {
    [GASS_HVIS]: false,
    [GASS_CLING]: false
  }
  dataValidtimeInfo = {
    [GASS_HVIS]: '---',
    [GASS_CLING]: '---'
  }
  dataVisible = {
    [GASS_HVIS]: false,
    [GASS_CLING]: false
  }
  dataMissing = {
    [GASS_HVIS]: false,
    [GASS_CLING]: false
  }

  // タイムコントローラ
  gassScaleTimeController;
  // タイムコントローラのリスト
  timeList;
  // 自動更新フラグ
  autoRefresh = true;

  ready = false;
  etd;
  elapsed = 0;
  dispatcher;
  visible = false;
  data = GASS_ALL;
  errorMessageInfo = {};

  constructor() {
    this.layerGassHvis = new WRAP.Geo.Layer(GASS_HVIS);
    this.layerGassCling = new WRAP.Geo.Layer(GASS_CLING);
    this.layerGassHvis.setVisible(false);
    this.layerGassCling.setVisible(false);
    this.layerGassHvis.disableInteraction(true);
    this.layerGassCling.disableInteraction(true);

    this.gassScaleTimeController = new TimeController(WX_ID_GASS_SCALE, {
      label: 'GASS Scale',
      getTimeList: () => {
        if (this.timeList) {
          return this.timeList;
        }
        this._updateTimeList();
        return this.timeList;
      },
      updated: () => {
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      },
      changeAutoRefresh: (autoFlg) => {
        this.setAutoRefresh(autoFlg);
      }
    });
  }

  setDispatcher(dispatcher) {
    this.dispatcher = dispatcher;
    this.missingDataObserver = new MissingDataObserver(this.dispatcher, MISSING_THRESHOLD, this.dataVisible);
  }

  async initialize() {
    await this.initData();
    this.initLayer();
    this.initTimeController();
    if (this.data === GASS_ALL) {
      this.dataInitialize[GASS_HVIS] = true;
      this.dataInitialize[GASS_CLING] = true;
    } else {
      this.dataInitialize[this.data] = true;
    }
  }

  initData() {
    return new Promise(resolveAll => {
      const dh = [];
      switch(this.data) {
        case GASS_HVIS:
          if (!this._isInitialized(GASS_HVIS)) {
            dh.push(this.dhGassHvis = new WRAP.Data(dhConfigGassHvis));
            this.missingDataObserver.monitoring(GASS_HVIS, this.dhGassHvis, this.layerGassHvis, {callback: this._updateMissing.bind(this)});
          }
          break;
        case GASS_CLING:
          if (!this._isInitialized(GASS_CLING)) {
            dh.push(this.dhGassCling = new WRAP.Data(dhConfigGassCling));
            this.missingDataObserver.monitoring(GASS_CLING, this.dhGassCling, this.layerGassCling, {callback: this._updateMissing.bind(this)});
          }
          break;
        case GASS_ALL:
          if (!this._isInitialized(GASS_HVIS)) {
            dh.push(this.dhGassHvis = new WRAP.Data(dhConfigGassHvis));
            this.missingDataObserver.monitoring(GASS_HVIS, this.dhGassHvis, this.layerGassHvis, {callback: this._updateMissing.bind(this)});
          }
          if (!this._isInitialized(GASS_CLING)) {
            dh.push(this.dhGassCling = new WRAP.Data(dhConfigGassCling));
            this.missingDataObserver.monitoring(GASS_CLING, this.dhGassCling, this.layerGassCling, {callback: this._updateMissing.bind(this)});
          }
          break;
        default:
          break;
      }

      dh.forEach(dh => {
        dh.validate((displayTime, data, content) => {
          return this._validateTime(displayTime, content);
        });
      });

      const promises = [];
      dh.forEach(dh => {
        promises.push(
          new Promise(resolve => {
            dh.updated('validtime', () => {
              this._selectValidTime();
              resolve();
            });
            const message = dh.conf.data.Name === 'GASS_HVIS' ? COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_HVIS : COMMUNICATE_ERROR_MESSAGE_WNI_SRF_VSCAL_GASS_CLING;
            dh.updated('timelist', () => {
              if (this.errorMessageInfo[dh.conf.data.Name]) {
                this.dispatcher({type: 'CLEAR_ERROR', payload: this.errorMessageInfo[dh.conf.data.Name]});
                this.errorMessageInfo[dh.conf.data.Name] = null;
              }
            }, () => {
              this.errorMessageInfo[dh.conf.data.Name] = this.dispatcher({type: 'SHOW_ERROR', payload: {
                message: message,
                additionalMessage: COMMUNICATE_ERROR_MESSAGE_TRY_AGAIN_LATER
              }});
            });
          })
        )
      });

      Promise.all(promises).then(() => {
        resolveAll();
      })
    });
  }

  initLayer() {
    const target = this._getTarget();
    target.layer.forEach((layer, index) => {
      if (!this._isInitialized(target.dataName[index])) {
        layer.configure(target.dh[index], target.conf[index]);
      }
    })
  }

  initTimeController() {
    const target = this._getTarget();
    target.layer.forEach((layer, index) => {
      if (!this._isInitialized(target.dataName[index])) {
        this.gassScaleTimeController.addLayer(layer);
      }
    })
  }

  _isInitialized(type) {
    if (type === GASS_ALL) {
      return this.dataInitialize[GASS_HVIS] && this.dataInitialize[GASS_CLING];
    }
    return this.dataInitialize[type];
  }

  async setData(data) {
    //現在選択中のautoUpdateをfalseに切り替える
    if (data !== this.data && this._isInitialized(this.data)) {
      //ALLが選択中の場合、引数ではない方を止める
      //HVISが選択中の場合、引数がCLINGであればHVISを止める。引数がALLであれば、何もしない
      //CLINGが選択中の場合、引数がHVISであればCLINGを止める。引数がALLであれば、何もしない
      switch (this.data) {
        case GASS_ALL:
          if (data === GASS_HVIS) {
            this.dhGassCling.setAutoUpdate('validtime', false);
            this.dhAutoUpdate[GASS_CLING] = false;
            this.dataVisible[GASS_CLING] = false;
          } else {
            this.dhGassHvis.setAutoUpdate('validtime', false);
            this.dhAutoUpdate[GASS_HVIS] = false;
            this.dataVisible[GASS_HVIS] = false;
          }
          break;
        case GASS_HVIS:
          if (data === GASS_CLING) {
            this.dhGassHvis.setAutoUpdate('validtime', false);
            this.dhAutoUpdate[GASS_HVIS] = false;
            this.dataVisible[GASS_HVIS] = false;
          }
          break;
        case GASS_CLING:
          if (data === GASS_HVIS) {
            this.dhGassCling.setAutoUpdate('validtime', false);
            this.dhAutoUpdate[GASS_CLING] = false;
            this.dataVisible[GASS_CLING] = false;
          }
          break;
        default:
      }
    }
    this.data = data;
    /*
     Contents Set対応
     setDataの処理後、drawLayerを呼び出さないと画面に反映されない。
     対応前はMenuでsetData後にdrawLayerを呼び出していた。
     それだとContentsSetを適用させた際にdrawLayerが呼び出されないためここでコールするように修正
     visible=falseの場合はContentsSet適用の処理からdrawLayerが呼ばれるためここではvisibleがtrueの場合に呼び出す対応を入れる
     */
    if (this.visible === true) {
      await this.drawLayer(true);
    }
  }

  getData() {
    return this.data;
  }

  getDataList() {
    return [
      {value: GASS_HVIS, label: "VIS"},
      {value: GASS_CLING, label: "CIG"},
      {value: GASS_ALL, label: "ALL"}
    ]
  }

  async drawLayer(visible) {
    this.visible = visible;
    if (this.data === GASS_ALL) {
      this.dataVisible[GASS_HVIS] = visible;
      this.dataVisible[GASS_CLING] = visible;
    } else {
      this.dataVisible[this.data] = visible;
    }

    if (!this._isInitialized(this.data)) {
      await this.initialize();
    }

    this.setVisible(visible);
  }

  getLayer() {
    return this._allLayers();
  }

  _allLayers() {
    return [
      this.layerGassHvis,
      this.layerGassCling
    ]
  }

  setVisible(visible) {
    if (!this._isInitialized(this.data)) {
      return;
    }

    const target = this._getTarget();

    this._allLayers().forEach(layer => {
      if (this.data === GASS_ALL && visible) {
        layer.show();
      }else if (layer._name === this.data) {
        if (visible) {
          layer.show();
        } else {
          layer.hide();
        }
      } else {
        layer.hide();
      }
    });

    const enabled = visible && this.autoRefresh;
    target.dataName.forEach((dataName,index) => {
      if (this.dhAutoUpdate[dataName] !== enabled) {
        target.dh[index].setAutoUpdate('validtime', enabled);
        this.dhAutoUpdate[dataName] = enabled;
      }
    });

    this._updateTimeList();
    this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
  }

  getTimeInformation() {
    const dataId = this.data === GASS_ALL ? [GASS_HVIS, GASS_CLING] : this.data;
    return {
      id: GassScale.NAME,
      label: 'GASS Scale',
      baseTime: {
        id: dataId,
        time: this.dataValidtimeInfo[this.data]
      }
    }
  }

  getLegend() {
    return {
      id: GassScale.NAME,
      label: 'GASS Scale',
      component: LegendGassScale,
      contents: this
    }
  }

  getTimeController() {
    return this.gassScaleTimeController;
  }

  applyElapsed(elapsed) {
    this.elapsed = elapsed;

    if (!this._isInitialized(this.data)) return;

    this._selectValidTime();
  }

  applyPlanETD(etd) {
    this.etd = etd;

    if (!this._isInitialized(this.data)) return;

    this._selectValidTime();
  }

  setAutoRefresh(autoFlg) {
    if (autoFlg === this.autoRefresh) return;

    this.autoRefresh = autoFlg;

    if (!this._isInitialized(this.data)) return;

    const target = this._getTarget();
    target.dataName.forEach((dataName,index) => {
      if (this.dhAutoUpdate[dataName] !== this.autoRefresh) {
        target.dh[index].setAutoUpdate('validtime', this.autoRefresh);
        this.dhAutoUpdate[dataName] = this.autoRefresh;
      }
    })
  }

  /**
   * 自動更新モード時のvalid選択
   */
  _selectValidTime() {
    let targetTime;
    if (!this.etd) {
      //プラン未選択時
      if (!this.autoRefresh) {
        targetTime = this.gassScaleTimeController.getCurrentTime();
      } else {
        targetTime = Date.now();
      }
    } else {
      //プラン選択時
      targetTime = this.etd.getTime() + this.elapsed * 1000;
    }

    const vt = this._nearValidTime(targetTime);
    if (vt) {
      if (this.dataValidtimeInfo[this.data] !== vt) {
        this.dataValidtimeInfo[this.data] = vt;
        this.dispatcher({type: 'SET_CONTENTS_TIME', payload: this.getTimeInformation()});
      }
      this._updateDataset();
    }
    this._updateTimeList();
  }

  /**
   * タイムコントロールモード時のvalid選択
   */
  _validateTime(displayTime, content) {
    const vt = this._nearValidTime(displayTime);
    if (vt) {
      this.dataValidtimeInfo[this.data] = content.validtime = vt;
      return true;
    }
    return false
  }

  _nearValidTime(targetTime) {
    let tempList = []
    this._getTarget().dh.forEach(dh => {
      const valid = dh.retained.validtime?.validtime;
      if (valid) {
        tempList = tempList.concat(valid);
      }
    });
    const timeList = Array.from(new Set(tempList));
    if (!timeList || timeList.length === 0) return null;

    let _targetTime;
    if (typeof targetTime === 'number') {
      _targetTime = targetTime
    } else {
      _targetTime = parseUTCDate(targetTime).getTime();
    }

    const tmpTimeList = timeList.reduce((accumulator, validtime) => {
      const validSec = parseUTCDate(validtime).getTime();
      accumulator.push(
        {
          "validtime": validtime,
          "validtime_msec": validSec
        }
      );
      return accumulator;
    }, []);

    this.latestValid = tmpTimeList[0].validtime;

    // targetTimeに最も近いvalidを選択する(直近が過去時制と未来時制で同時刻の場合、未来時制のValidデータを採用する。)
    const sortedTimelist = _orderBy(tmpTimeList, [
      o => {
        return Math.abs(_targetTime - o.validtime_msec);
      },
      'validtime'
    ], ['asc', 'desc']);

    const selectedTime = sortedTimelist[0];

    return selectedTime.validtime;
  }

  _updateDataset() {
    if (this.dataValidtimeInfo[this.data] !== '---') {
      /**
       * WRAP, CloudFrontのキャッシュ対策としてクエリストリングに最新のvalidtimeを付与する
       * timelistが更新された場合にvalidtimeを更新する
       */
      let latestValid;
      if (this.timeList && this.timeList[this.timeList.length - 1] !== this.latestValid) {
        latestValid = this.latestValid;
      } else {
        latestValid = this.dataValidtimeInfo[this.data];
      }
      const target = this._getTarget();
      target.layer.forEach(layer => {
        layer.set({validtime: this.dataValidtimeInfo[this.data], latestValid: latestValid});
      });
    }
  }

  _updateTimeList() {
    //表示状態
    const visibleData = [GASS_HVIS, GASS_CLING].filter(data => this.dataVisible[data]);
    //表示状態＆欠測していない。
    const noMissingData = visibleData.filter(data => !this.dataMissing[data]);
    //すべて欠測であれば、表示状態onの先頭
    const target = noMissingData[0] || visibleData[0];
    const dh = target === GASS_HVIS ? this.dhGassHvis : this.dhGassCling;
    let validTimes = dh?.retained?.validtime?.validtime;
    if (!validTimes) {
      return;
    }

    // 最新から直近過去までのvalidにfilter
    const now = new Date();
    now.setMinutes(0, 0, 0);
    const base = formatUTCDate(now, "%Y%m%dT%H%M%S");
    validTimes = Array.from(validTimes).sort();
    this.timeList = validTimes.filter(vt => {
      return base <= vt;
    });

    if (this.autoRefresh) {
      this.gassScaleTimeController.setCurrentTime(this.dataValidtimeInfo[this.data]);
    }
    this.gassScaleTimeController.adjustCurrentTime();
    this.gassScaleTimeController.invalidate();
  }

  _getTarget() {
    let dh;
    let layer;
    let dataName;
    let conf;
    let autoUpdate;
    switch(this.data) {
      case GASS_HVIS:
        dataName = [GASS_HVIS];
        dh = [this.dhGassHvis];
        layer = [this.layerGassHvis];
        conf = [layerConfigHvis];
        autoUpdate = [this.dhAutoUpdate[GASS_HVIS]]
        break;
      case GASS_CLING:
        dataName = [GASS_CLING];
        dh = [this.dhGassCling];
        layer = [this.layerGassCling];
        conf = [layerConfigCling];
        autoUpdate = [this.dhAutoUpdate[GASS_CLING]]
        break;
      case GASS_ALL:
        dataName = [GASS_HVIS, GASS_CLING];
        dh = [this.dhGassHvis, this.dhGassCling];
        layer = [this.layerGassHvis, this.layerGassCling];
        conf = [layerConfigHvis, layerConfigCling];
        autoUpdate = [this.dhAutoUpdate[GASS_HVIS], this.dhAutoUpdate[GASS_CLING]]
        break;
      default:
        break;
    }
    return {
      dataName: dataName,
      dh: dh,
      layer: layer,
      conf: conf,
      autoUpdate: autoUpdate
    }
  }

  _updateMissing(data, missing) {
    this.dataMissing[data] = missing;
  }
}

export default GassScale;
